import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

interface HistoryRowDto {
  id: number;
  conversionId: string;
  modeId: string;
  parameter: string;
  status: string;
  outputFileName?: string;
  auditFileName: string;
  sha256?: string;
  rataVerdict?: string;
  inputFiles?: string;
  createdAt: string;
  createdBy?: string;
}

interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/**
 * Every output this user has emitted (admins see everyone's), persisted at emit time — the
 * place to re-download a file or its audit sidecar after the session is gone. FAILED rows are
 * the forensic audits of conversions that wrote nothing.
 */
export function History() {
  const { data, isLoading } = useQuery({
    queryKey: ['history'],
    queryFn: () => apiClient.get<PagedResult<HistoryRowDto>>('/api/history?pageSize=100'),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;

  const rows = data?.items ?? [];
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-atg-navy">Conversion history</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-600">Nothing has been converted yet.</p>
      ) : (
        <table className="text-sm w-full border-collapse bg-white border border-slate-200 rounded-md">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="p-2">When</th>
              <th className="p-2">Test</th>
              <th className="p-2">Result</th>
              <th className="p-2">File</th>
              <th className="p-2">Inputs</th>
              <th className="p-2">Downloads</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="p-2 whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
                <td className="p-2">{r.parameter} <span className="text-slate-400">({r.modeId})</span></td>
                <td className={`p-2 font-medium ${
                  r.status === 'failed' ? 'text-red-700'
                    : r.rataVerdict === 'FAILED' ? 'text-red-700' : 'text-green-700'}`}>
                  {r.status === 'failed' ? 'nothing written' : r.rataVerdict ?? 'written'}
                </td>
                <td className="p-2 font-mono text-xs">{r.outputFileName ?? r.auditFileName}</td>
                <td className="p-2 text-xs text-slate-500">{r.inputFiles}</td>
                <td className="p-2 space-x-3 whitespace-nowrap">
                  {r.outputFileName && (
                    <a className="text-atg-blue hover:underline" href={`/api/history/${r.id}/output`}>JSON</a>
                  )}
                  <a className="text-atg-blue hover:underline" href={`/api/history/${r.id}/audit`}>Audit</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
